/**
 * Helper SEO / Open Graph per le pagine pubbliche (SPA).
 *
 * ★ Fase 89 — Ogni link e sub-link funnel ha la sua thumbnail di condivisione
 * (WhatsApp / Telegram / Facebook / LinkedIn / X). In produzione le route
 * pubbliche vengono servite ai crawler da un renderer che esegue il JS, quindi
 * i meta tag impostati al mount (title, description, og:image, og:url,
 * canonical…) sono quelli che i social leggono.
 *
 * L'index.html contiene i default italiani (/og/og-funnel.jpg, og:url = "/"):
 * ogni pagina li sovrascrive con `setPageMeta()`.
 */

/** Base URL assoluta del sito (stessa usata da index.html via %REACT_APP_BACKEND_URL%). */
const SITE_URL = (() => {
  const env = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  if (env) return env;
  return typeof window !== 'undefined' ? window.location.origin : '';
})();

/* ── Registry immagini OG (1200×630 JPEG, generate da scripts/generate_og_images.py) ──
   Chiave = slug funnel (/f/<slug>). Le immagini derivano dalle CARD degli asset. */
const OG = {
  funnelIT: '/og/og-funnel.jpg',
  funnelES: '/og/og-funnel-es.jpg',
  funnelEN: '/og/og-funnel-en.jpg',
  narea: '/og/og-narea.jpg',
  threeGarden: '/og/og-three-garden.jpg',
  lombok: '/og/og-lombok.jpg',
  bali: '/og/og-bali.jpg',
  thailand: '/og/og-thailand.jpg',
};

export const OG_IMAGES = {
  default: OG.funnelIT,
  // Funnel principali
  it: OG.funnelIT,
  es: OG.funnelES,
  en: OG.funnelEN,
  'it-al': OG.funnelIT,
  'en-al': OG.funnelEN,
  // Albania · Narea
  'it-al-na': OG.narea,
  'es-al-na': OG.narea,
  'en-al-na': OG.narea,
  // Albania · Three Garden Qerret
  'it-al-tg': OG.threeGarden,
  'es-al-tg': OG.threeGarden,
  'en-al-tg': OG.threeGarden,
  // Indonesia · Lombok
  'it-lo': OG.lombok,
  'es-lo': OG.lombok,
  'en-lo': OG.lombok,
  'es-id': OG.lombok,
  'en-id': OG.lombok,
  // Indonesia · Bali
  'it-ba': OG.bali,
  'es-ba': OG.bali,
  'en-ba': OG.bali,
  // Thailandia
  'es-th': OG.thailand,
  'en-th': OG.thailand,
};

/** Immagine OG per uno slug funnel (fallback: immagine brand IT). */
export function ogImageFor(slug) {
  const key = String(slug || '').trim().toLowerCase();
  return OG_IMAGES[key] || OG_IMAGES.default;
}

const LOCALES = { it: 'it_IT', es: 'es_ES', en: 'en_GB' };

function absUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

function mimeFor(url) {
  const u = String(url || '').toLowerCase();
  if (u.endsWith('.png')) return 'image/png';
  if (u.endsWith('.webp')) return 'image/webp';
  return 'image/jpeg';
}

function upsertMeta(attr, key, content) {
  if (typeof document === 'undefined' || content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
}

function upsertCanonical(href) {
  if (typeof document === 'undefined' || !href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Aggiorna la meta description (+ og/twitter). Mantenuta per retro-compatibilità.
 */
export function setMetaDescription(text) {
  if (!text) return;
  upsertMeta('name', 'description', text);
  upsertMeta('property', 'og:description', text);
  upsertMeta('name', 'twitter:description', text);
}

/**
 * Imposta TUTTI i meta tag social della pagina corrente.
 *
 * @param {object} opts
 * @param {string} [opts.title]        <title> + og:title + twitter:title
 * @param {string} [opts.description]  description + og:description + twitter:description
 * @param {string} [opts.image]        path (es. /og/og-narea.jpg) o URL assoluto
 * @param {string} [opts.imageAlt]     og:image:alt + twitter:image:alt (default: title)
 * @param {string} [opts.path]         path della pagina (default: location.pathname) → og:url + canonical
 * @param {string} [opts.locale]       'it' | 'es' | 'en' (→ og:locale)
 */
export function setPageMeta({ title, description, image, imageAlt, path, locale } = {}) {
  if (typeof document === 'undefined') return;

  if (title) {
    document.title = title;
    upsertMeta('property', 'og:title', title);
    upsertMeta('name', 'twitter:title', title);
  }
  if (description) setMetaDescription(description);

  const pagePath = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const pageUrl = absUrl(pagePath);
  upsertMeta('property', 'og:url', pageUrl);
  upsertCanonical(pageUrl);

  if (image) {
    const imgUrl = absUrl(image);
    upsertMeta('property', 'og:image', imgUrl);
    upsertMeta('property', 'og:image:secure_url', imgUrl);
    upsertMeta('property', 'og:image:type', mimeFor(imgUrl));
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('name', 'twitter:image', imgUrl);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    const alt = imageAlt || title;
    if (alt) {
      upsertMeta('property', 'og:image:alt', alt);
      upsertMeta('name', 'twitter:image:alt', alt);
    }
  }

  if (locale) {
    upsertMeta('property', 'og:locale', LOCALES[locale] || locale);
    document.documentElement.setAttribute('lang', String(locale).slice(0, 2));
  }
}
